import React, { useEffect, useState, useRef } from 'react';
import { Table, Spin, message, Button, Modal, Tooltip, Popconfirm, Upload, Space } from 'antd';
import { CloudDownloadOutlined, FileImageOutlined, FilePdfOutlined, FileTextOutlined, FileOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import axios from '../../../config/axiosInstance';

const FilesTab = ({ participantId, host }) => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filePreview, setFilePreview] = useState(null);

    const fetchFiles = async () => {
        try {
            const tokenRes = await axios.get(`/regasus/token?host=${host}`);
            const token = tokenRes.data.access_token;

            if (!token) {
                message.error('No token received');
                return;
            }

            const res = await axios.get(`/regasus/participants/${participantId}/files`, {
                params: {
                    token,
                    host,
                }
            });

            const filesRaw = res.data?.fileSummaries;

            const parsed = Array.isArray(filesRaw)
                ? filesRaw
                : filesRaw?.fileSummary
                    ? Array.isArray(filesRaw.fileSummary)
                        ? filesRaw.fileSummary
                        : [filesRaw.fileSummary]
                    : [];

            setFiles(parsed);
        } catch (err) {
            console.error(err);
            message.error('Failed to load files');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFiles();
    }, [participantId, host]);

    // Function to obtain the contents of the file
    const fetchFileContent = async (fileId, fileName) => {
        try {
            if (!host || !participantId || !fileId || !fileName) {
                console.error('❌ Missing required parameters: host, participantId, fileId, or fileName');
                message.error('Missing required parameters');
                return;
            }

            const tokenRes = await axios.get(`/regasus/token?host=${host}`);
            const token = tokenRes.data.access_token;

            if (!token) {
                message.error('No token received');
                return;
            }

            const response = await axios.get(`/regasus/participants/${participantId}/files/${fileId}/content`, {
                params: {
                    token,
                    host,
                },
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/octet-stream',
                },
                responseType: 'blob',
            });

            return response;
        } catch (error) {
            console.error('Error fetching file content:', error);
            message.error('Failed to fetch file content');
        }
    };

    const downloadFile = async (fileId, fileName) => {
        const response = await fetchFileContent(fileId, fileName);

        if (response) {
            const blob = new Blob([response.data], { type: 'application/octet-stream' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = fileName || 'downloaded_file';
            link.click();
        }
    };

    const viewFile = async (fileId, fileName) => {
        const response = await fetchFileContent(fileId, fileName);

        if (response) {
            const fileURL = URL.createObjectURL(response.data);
            const fileExtension = fileName.split('.').pop().toLowerCase();

            if (fileExtension === 'pdf' || fileExtension === 'jpg' || fileExtension === 'jpeg' || fileExtension === 'png') {
                setFilePreview(fileURL);
            } else {
                message.warning('This file cannot be previewed in the browser');
            }
        }
    };

    const deleteFile = async (fileId) => {
        try {
            const tokenRes = await axios.get(`/regasus/token?host=${host}`);
            const token = tokenRes.data.access_token;

            if (!token) {
                message.error('No token received');
                return;
            }

            const response = await axios.delete(`/regasus/files/${fileId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                data: {
                    token,
                    host,
                }
            });

            message.success('File deleted successfully');
            fetchFiles();
        } catch (error) {
            console.error('Error deleting file:', error);
            message.error('Failed to delete file');
        }
    };

    const handleFileUpload = async (file) => {
        const tokenRes = await axios.get(`/regasus/token?host=${host}`);
        const token = tokenRes.data.access_token;

        if (!token) {
            message.error('No token received');
            return;
        }

        // Create a FormData with the required file and parameters
        const formData = new FormData();
        formData.append('file', file);
        formData.append('token', token);
        formData.append('host', host);
        formData.append('name', file.name);
        formData.append('description', 'File uploaded from Dsync');
        formData.append('language', 'en');

        try {
            const response = await axios.post(`/regasus/participants/${participantId}/files`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            message.success('File uploaded successfully');
            fetchFiles();
        } catch (error) {
            console.error('Error uploading file:', error);
            message.error('Failed to upload file');
        }
    };

    // Configuration of the file upload component
    const uploadProps = {
        customRequest: ({ file, onSuccess, onError }) => {
            handleFileUpload(file);
            onSuccess();
        },
        showUploadList: false,
    };

    // Get the file icon according to its extension
    const getFileIcon = (fileName) => {
        const fileExtension = fileName.split('.').pop().toLowerCase();
        switch (fileExtension) {
            case 'jpg':
            case 'jpeg':
            case 'png':
                return <FileImageOutlined style={{ color: 'green', fontSize: '20px' }} />;
            case 'pdf':
                return <FilePdfOutlined style={{ color: 'red', fontSize: '20px' }} />;
            case 'txt':
            case 'doc':
            case 'docx':
            case 'odt':
                return <FileTextOutlined style={{ color: '#00BFFF', fontSize: '20px' }} />;
            default:
                return <FileOutlined style={{ color: 'orange', fontSize: '20px' }} />;
        }
    };

    const handleUpload = (file) => {
        if (file) {
            handleFileUpload(file);  // Call the function to upload the file
            message.success('Photo uploaded successfully!');
        } else {
            message.error('No photo captured');
        }
    };

    const columns = [
        {
            title: 'File Name',
            dataIndex: 'name',
            key: 'name',
            render: (text) => {
                const truncatedName = text.length > 50 ? `${text.substring(0, 40)}...` : text;
                return (
                    <Tooltip title={text}>
                        <span>{truncatedName}</span>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Size',
            key: 'size',
            render: (_, record) => {
                const value = record?.size;
                return value != null ? `${(value / 1024 / 1024).toFixed(2)} MB` : '—';
            }
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => {
                const fileExtension = record?.name.split('.').pop().toLowerCase();
                return (
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Space size="small">
                            {/* Show only the view file button if it is a preview file */}
                            {(fileExtension === 'pdf' || fileExtension === 'jpg' || fileExtension === 'jpeg' || fileExtension === 'png') && (
                                <Tooltip title="View File">
                                    <Button
                                        type="link"
                                        onClick={() => viewFile(record?.ID, record?.name)}
                                        style={{ fontSize: '20px' }}
                                    >
                                        {getFileIcon(record?.name)}
                                    </Button>
                                </Tooltip>
                            )}
                            {/* File download button */}
                            <Tooltip title="Download File">
                                <Button
                                    type="link"
                                    onClick={() => downloadFile(record?.ID, record?.name)}
                                    style={{ fontSize: '20px', marginLeft: '10px' }}
                                >
                                    <CloudDownloadOutlined />
                                </Button>
                            </Tooltip>
                            {/* Delete file button */}
                            <Popconfirm
                                title="Are you sure you want to delete this file?"
                                onConfirm={() => deleteFile(record?.ID)}
                                okText="Yes"
                                cancelText="No"
                            >
                                <Tooltip title="Delete File">
                                    <Button
                                        type="link"
                                        style={{ fontSize: '20px', marginLeft: '10px' }}
                                    >
                                        <DeleteOutlined style={{ color: 'red' }} />
                                    </Button>
                                </Tooltip>
                            </Popconfirm>
                        </Space>
                    </div>
                );
            },
        }
    ];

    return loading ? (
        <Spin />
    ) : (
        <>
            {/* Upload Button */}
            <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />} type="primary" style={{ marginBottom: '20px' }}>
                    Upload New Document
                </Button>
            </Upload>

            <Table
                size="small"
                scroll={{ x: 'max-content' }}
                dataSource={files.map((file, i) => ({
                    key: file.ID || i,
                    name: file.name,
                    description: file.description,
                    fileType: file.fileType,
                    size: file.size,
                    externalPath: file.externalPath,
                    ID: file.ID,
                }))}
                columns={columns}
                pagination={{ pageSize: 10 }}
            />
            {/* Modal to display file preview */}
            {filePreview && (
                <Modal
                    visible={true}
                    title="File Preview"
                    footer={null}
                    onCancel={() => setFilePreview(null)}
                    width={800}
                >
                    <iframe src={filePreview} width="100%" height="500px" title="File Preview" />
                </Modal>
            )}

        </>
    );
};

export default FilesTab;
